import { useState, useCallback, useRef, useEffect } from 'react';
import { BuscarGlobal } from '../../../Actions/Usuarios/BuscadorGlobalActions';
import { GnlUrlBaseModulosApp } from '../../../Configuracion/Llaves/RepositorioConfig';
import { normalizarResultado } from '../utils/buscadorUtils';

/**
 * Hook para la lógica de búsqueda global del buscador.
 * 
 * @param {Object} options - Opciones del hook
 * @param {string} options.usuarioId - ID del usuario
 * @param {string} options.companiaId - ID de la compañía
 * @param {string} options.idioma - Idioma del usuario
 * @param {Function} options.onNavigate - Callback cuando se navega a un item
 * @param {Function} options.onGuardarReciente - Callback para guardar en recientes
 * 
 * @returns {Object} Estado y handlers para el buscador
 * 
 * @example
 * const {
 *   textoBusqueda, setTextoBusqueda,
 *   resultados, cargando,
 *   realizarBusqueda, limpiarBusqueda
 * } = useBusquedaGlobal({ usuarioId, companiaId, idioma });
 */
export const useBusquedaGlobal = ({ usuarioId, companiaId, idioma, onNavigate, onGuardarReciente }) => {
  const [textoBusqueda, setTextoBusqueda] = useState('');
  const [resultados, setResultados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [textoPrevio, setTextoPrevio] = useState('');

  const debounceRef = useRef(null);

  // Cleanup del debounce
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  /**
   * Realiza la búsqueda en el API.
   * @param {string} texto - Texto a buscar
   */
  const realizarBusqueda = useCallback(async (texto) => {
    if (!texto || texto.trim().length < 2 || !usuarioId) {
      setResultados([]);
      setCargando(false);
      return;
    }

    setCargando(true);

    try {
      const respuesta = await BuscarGlobal({
        texto: texto.trim(),
        usuarioId,
        companiaId,
        idioma: idioma || 'ES',
        urlBase: GnlUrlBaseModulosApp,
      });

      if (respuesta.status === 200 && respuesta.data) {
        const normalizados = Array.isArray(respuesta.data)
          ? respuesta.data.map(normalizarResultado)
          : [];
        setResultados(normalizados);
      } else {
        setResultados([]);
      }
    } catch (error) {
      console.error('Error en busqueda global:', error);
      setResultados([]);
    } finally {
      setCargando(false);
    }
  }, [usuarioId, companiaId, idioma]);

  /**
   * Maneja el cambio de texto con debounce.
   * @param {string} texto - Nuevo texto
   */
  const manejarCambioTexto = useCallback((texto) => {
    setTextoBusqueda(texto);
    setTextoPrevio(texto);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!texto || texto.trim().length === 0) {
      setResultados([]);
      setCargando(false);
      return;
    }

    setCargando(true);

    debounceRef.current = setTimeout(() => {
      realizarBusqueda(texto);
    }, 300);
  }, [realizarBusqueda]);

  /**
   * Limpia el estado de búsqueda.
   */
  const limpiarBusqueda = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setTextoBusqueda('');
    setResultados([]);
    setTextoPrevio('');
    setCargando(false);
  }, []);

  /**
   * Maneja el click en un resultado.
   * @param {Object} item - Item clickeado
   */
  const manejarItemClick = useCallback((item) => {
    const esUrlAbsoluta = item.path && item.path.startsWith('http');

    // Guardar en recientes
    if (onGuardarReciente) {
      onGuardarReciente(item);
    }

    // Navegar
    if (onNavigate) {
      onNavigate(item, esUrlAbsoluta);
    }
  }, [onGuardarReciente, onNavigate]);

  // Estados derivados
  const tieneResultados = resultados && resultados.length > 0;
  const debeMostrarNoResultados =
    !cargando && !tieneResultados && textoPrevio && textoPrevio.length >= 2;

  return {
    // Estado
    textoBusqueda,
    resultados,
    cargando,
    textoPrevio,
    // Setters
    setTextoBusqueda,
    // Handlers
    realizarBusqueda,
    manejarCambioTexto,
    limpiarBusqueda,
    manejarItemClick,
    // Estados derivados
    tieneResultados,
    debeMostrarNoResultados,
  };
};

export default useBusquedaGlobal;
